import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookHeart, Search } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import {
  booksApi,
  circulationApi,
  libraryFinesApi,
  libraryMembersApi,
  reservationsApi,
} from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { formatDate } from '@/utils/format';
import {
  Blockers,
  FineStatusBadge,
  LoanStatusBadge,
  Money,
  ReservationStatusBadge,
  Stat,
  authorsOf,
  overdueText,
} from './shared';

/**
 * The library, as a reader sees it.
 *
 * <p>One screen rather than four, because a reader has one question with four
 * parts: what have I got, when is it back, what am I waiting for, and do I owe
 * anything. Splitting that across four menu items would be organising the app
 * around the server's tables rather than around the person using it.
 *
 * <p>Everything here is scoped to the signed-in account by the server. This
 * screen sends no member identifier and could not read somebody else's loans by
 * changing a parameter, which is the only way that guarantee is worth anything.
 */
export function MyLibraryPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('title,asc', 10);

  const mayReserve = can(PERMISSIONS.LIBRARY_SELF_BORROW);
  const seesFines = can(PERMISSIONS.LIBRARY_SELF_VIEW);

  const [searching, setSearching] = useState(false);

  const memberships = useQuery({
    queryKey: queryKeys.libraryMembers.mine,
    queryFn: () => libraryMembersApi.mine(),
  });

  const membership = memberships.data?.[0];

  const loans = useQuery({
    queryKey: queryKeys.circulation.memberLoans(membership?.id ?? ''),
    queryFn: () => circulationApi.memberLoans(membership!.id),
    enabled: membership !== undefined,
  });

  const reservations = useQuery({
    queryKey: queryKeys.reservations.mine,
    queryFn: () => reservationsApi.mine(),
  });

  const fines = useQuery({
    queryKey: queryKeys.libraryFines.mine,
    queryFn: () => libraryFinesApi.mine(),
    enabled: seesFines,
  });

  const books = useQuery({
    queryKey: queryKeys.books.search(table.params),
    queryFn: () => booksApi.search(table.params),
    enabled: searching && table.searchInput !== '',
  });

  const renew = useMutation({
    mutationFn: (loanId: string) => circulationApi.renew(loanId),
    onSuccess: (loan) => {
      toast.success(`Renewed — now due back ${formatDate(loan.dueDate)}`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.circulation.all });
    },
    onError: (error) => handleApiError(error),
  });

  const reserve = useMutation({
    mutationFn: (bookId: string) => reservationsApi.reserveForSelf({ bookId }),
    onSuccess: (reservation) => {
      toast.success(
        `You are number ${reservation.queuePosition} in the queue for ${reservation.bookTitle}`,
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.books.all });
    },
    onError: (error) => handleApiError(error),
  });

  const cancelReservation = useMutation({
    mutationFn: (id: string) => reservationsApi.cancel(id, 'Cancelled by the reader'),
    onSuccess: () => {
      toast.info('Reservation cancelled');
      void queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
    },
    onError: (error) => handleApiError(error),
  });

  if (memberships.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (membership === undefined) {
    return (
      <div>
        <PageHeader
          title="My library"
          icon={<BookHeart className="h-5 w-5" aria-hidden="true" />}
        />
        <Card className="p-6 text-center text-sm text-muted">
          You do not have a library membership yet. The library can set one up for you.
        </Card>
      </div>
    );
  }

  const owed = (fines.data ?? []).filter(
    (fine) => fine.status === 'OUTSTANDING' || fine.status === 'PARTIALLY_SETTLED',
  );

  return (
    <div>
      <PageHeader
        title="My library"
        description={`${membership.membershipNumber} · ${membership.libraryName}`}
        icon={<BookHeart className="h-5 w-5" aria-hidden="true" />}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Books out"
          value={`${membership.booksOut} of ${membership.borrowingLimit}`}
          hint={`${membership.loanDays} days at a time`}
        />
        <Stat
          label="Waiting for"
          value={
            (reservations.data ?? []).filter(
              (row) => row.status === 'PENDING' || row.status === 'READY',
            ).length
          }
        />
        <Stat
          label="Ready to collect"
          value={(reservations.data ?? []).filter((row) => row.status === 'READY').length}
          tone={
            (reservations.data ?? []).some((row) => row.status === 'READY') ? 'success' : 'neutral'
          }
        />
        {seesFines && (
          <Stat
            label="You owe"
            value={<Money value={owed.reduce((sum, fine) => sum + fine.outstanding, 0)} />}
            tone={owed.length > 0 ? 'warning' : 'neutral'}
            hint={owed.length > 0 ? 'Payable with your school fees' : 'Nothing owed'}
          />
        )}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            What you have out
          </h2>
          {loans.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (loans.data ?? []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">Nothing out at the moment.</p>
          ) : (
            <ul className="mt-2 divide-y divide-line">
              {(loans.data ?? []).map((loan) => (
                <li key={loan.id} className="flex flex-wrap items-center gap-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">
                      {loan.bookTitle}
                    </span>
                    <span
                      className={`block text-[11px] ${
                        loan.overdue ? 'text-[color:var(--sms-danger)]' : 'text-subtle'
                      }`}
                    >
                      {loan.overdue
                        ? overdueText(loan.daysOverdue)
                        : `back by ${formatDate(loan.dueDate)}`}
                    </span>
                  </span>
                  <LoanStatusBadge status={loan.status} overdue={loan.overdue} />
                  {loan.renewable ? (
                    <Button size="sm" variant="secondary" onClick={() => renew.mutate(loan.id)}>
                      Renew
                    </Button>
                  ) : (
                    <span className="text-[11px] text-muted">
                      {loan.renewalBlockers[0] ?? 'Cannot renew'}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              What you are waiting for
            </h2>
            {(reservations.data ?? []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">
                You can reserve a book once every copy of it is out.
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-line">
                {(reservations.data ?? []).map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center gap-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{row.bookTitle}</span>
                      <span className="block text-[11px] text-subtle">
                        {row.status === 'PENDING'
                          ? `number ${row.queuePosition} in the queue`
                          : row.status === 'READY'
                            ? `waiting for you until ${
                                row.collectBy === undefined ? '—' : formatDate(row.collectBy)
                              }`
                            : formatDate(row.requestedAt)}
                      </span>
                    </span>
                    <ReservationStatusBadge status={row.status} />
                    {(row.status === 'PENDING' || row.status === 'READY') && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancelReservation.mutate(row.id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {seesFines && (
            <Card className="p-4">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                What you owe
              </h2>
              {(fines.data ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">Nothing owed.</p>
              ) : (
                <>
                  <ul className="mt-2 divide-y divide-line">
                    {(fines.data ?? []).map((fine) => (
                      <li key={fine.id} className="flex flex-wrap items-center gap-3 py-2">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-ink">{fine.reason}</span>
                          <span className="block text-[11px] text-subtle">
                            {formatDate(fine.chargedOn)}
                          </span>
                        </span>
                        <Money value={fine.outstanding} />
                        <FineStatusBadge status={fine.status} />
                      </li>
                    ))}
                  </ul>
                  {owed.length > 0 && (
                    <p className="mt-3 border-t border-line pt-3 text-xs text-muted">
                      Library fines are added to your school fee account and are paid the same way
                      as everything else. There is nothing to pay at the library desk.
                    </p>
                  )}
                </>
              )}
            </Card>
          )}
        </div>
      </div>

      {mayReserve && (
        <Card className="mt-4 p-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Find a book
              </h2>
              <p className="mt-0.5 text-xs text-muted">
                Search the whole catalogue by title, subject or author.
              </p>
            </div>
          </div>

          <label className="mt-3 block">
            <span className="sr-only">Search the catalogue</span>
            <span className="flex items-center gap-2 border border-line bg-bg px-3 py-2 focus-within:border-accent">
              <Search className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
              <input
                className="w-full bg-transparent text-base text-ink outline-none"
                value={table.searchInput}
                placeholder="What are you looking for?"
                onChange={(event) => {
                  table.setSearchInput(event.target.value);
                  setSearching(true);
                }}
              />
            </span>
          </label>

          {searching && table.searchInput !== '' && (
            <div className="mt-3">
              {books.isPending ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : (books.data?.data ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">
                  Nothing matched. The search copes with a misspelling, so it is probably not here.
                </p>
              ) : (
                <ul className="divide-y divide-line">
                  {(books.data?.data ?? []).map((book) => (
                    <li key={book.id} className="flex flex-wrap items-center gap-3 py-2">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-ink">
                          {book.title}
                        </span>
                        <span className="block text-[11px] text-subtle">
                          {authorsOf(book.authors)}
                        </span>
                      </span>
                      <span className="text-xs text-muted">
                        {book.availability.availableCopies > 0
                          ? `${book.availability.availableCopies} on the shelf`
                          : book.availability.queueLength > 0
                            ? `${book.availability.queueLength} waiting`
                            : 'all out'}
                      </span>
                      {book.availability.availableCopies === 0 &&
                        book.availability.totalCopies > 0 && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => reserve.mutate(book.id)}
                            disabled={reserve.isPending}
                          >
                            Reserve
                          </Button>
                        )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>
      )}

      {membership.status !== 'ACTIVE' && (
        <div className="mt-4">
          <Blockers
            reasons={[`Your membership is ${membership.status.toLowerCase()}.`]}
            title="You cannot borrow at the moment"
          />
        </div>
      )}
    </div>
  );
}
